import { COLORS } from '../../constants/theme';

/**
 * SF-122: one weighted stat line — used for Standing (land/networth/honour), Power
 * (offense/defense), and any future page's "ranked stat list." `weight` is the mechanism by
 * which a card expresses "this number matters more than that one" (visual size only — tests
 * assert the `data-weight` attribute, not computed font size).
 */
export interface MetricRowRankPill {
  rank: number;
  total: number;
  pending?: boolean;
}

interface MetricRowProps {
  label: string;
  value: string | number;
  unit: string;
  weight: 'primary' | 'secondary' | 'tertiary';
  rankPill?: MetricRowRankPill;
  /**
   * Deviation from the spec's prop table: an optional color override, needed for the Resources
   * card's Trade row ("signed, colored per existing ResourceBar convention" — Architecture
   * Integration), which `weight` alone can't express. Falls back to the weight-driven color when
   * omitted, so every spec-literal usage is unaffected.
   */
  valueColor?: string;
}

const WEIGHT_STYLES: Record<MetricRowProps['weight'], { fontSize: number; fontWeight: number; color: string }> = {
  primary:   { fontSize: 20, fontWeight: 700, color: COLORS.text.primary },
  secondary: { fontSize: 16, fontWeight: 600, color: COLORS.text.secondary },
  tertiary:  { fontSize: 13, fontWeight: 500, color: COLORS.text.tertiary },
};

export function MetricRow({ label, value, unit, weight, rankPill, valueColor }: MetricRowProps) {
  const style = WEIGHT_STYLES[weight];

  return (
    <div className="flex items-center justify-between" data-weight={weight}>
      {/* CLAUDE.md: label-unit-value layout — unit sits next to the label, not appended to the
          value, so the value column stays numeric-only and comparable down the column. */}
      <span className="text-sm text-game-400">
        {label}
        {unit && ` (${unit})`}
      </span>
      <div className="flex items-center gap-2">
        <span
          className="font-mono"
          style={{ fontSize: style.fontSize, fontWeight: style.fontWeight, color: valueColor ?? style.color, fontVariantNumeric: 'tabular-nums' }}
        >
          {value}
        </span>
        {rankPill && (
          <span
            className="text-[10px] font-mono px-1.5 py-0.5 rounded"
            style={
              rankPill.pending
                ? { border: `1px dashed ${COLORS.text.muted}`, color: COLORS.text.muted }
                : { border: `1px solid ${COLORS.link}`, color: COLORS.link }
            }
          >
            {rankPill.pending ? 'pending' : `#${rankPill.rank}/${rankPill.total}`}
          </span>
        )}
      </div>
    </div>
  );
}
